import React, { useMemo } from 'react';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import type { HomeVehicleCard } from './homeTypes';

const FALLBACK_TILES = [
  { id: 'brand-1', image: '/images/brand/hero-garage-golden-hour.jpg', label: 'Garage' },
  { id: 'brand-2', image: '/images/brand/workshop-mod-detail.jpg', label: 'Workshop' },
  { id: 'brand-3', image: '/images/brand/paddock-meet-dusk.jpg', label: 'Paddock' },
  { id: 'brand-4', image: '/images/brand/home-event-chapter-wide.jpg', label: 'Events' },
  { id: 'brand-5', image: '/images/brand/story-mountain-road.jpg', label: 'Road' },
  { id: 'brand-6', image: '/images/brand/home-build-record-wide.jpg', label: 'Record' },
  { id: 'brand-7', image: '/images/brand/home-share-profile.jpg', label: 'Share' },
  { id: 'brand-8', image: '/images/brand/empty-garage-bay.jpg', label: 'Bay' },
  { id: 'brand-9', image: '/images/brand/home-club-community.jpg', label: 'Club' },
];

type MosaicTile =
  | { kind: 'vehicle'; vehicle: HomeVehicleCard }
  | { kind: 'brand'; id: string; image: string; label: string };

interface HeroBuildMosaicProps {
  vehicles: HomeVehicleCard[];
  featuredVehicleId?: number | null;
}

function tileTitle(tile: MosaicTile): string {
  if (tile.kind === 'brand') return tile.label;
  const v = tile.vehicle;
  return v.nickname || v.display_name || `${v.year} ${v.make} ${v.model}`;
}

function tileSubtitle(tile: MosaicTile): string | null {
  if (tile.kind === 'brand') return 'On Bilt';
  const v = tile.vehicle;
  const chassis = [v.year, v.make, v.model].filter(Boolean).join(' ');
  if (v.nickname && chassis) return chassis;
  return v.owner_username ? `@${v.owner_username}` : null;
}

function tileImage(tile: MosaicTile): string | null {
  if (tile.kind === 'brand') return tile.image;
  return resolvePublicFileUrl(tile.vehicle.image_url);
}

const MosaicCard: React.FC<{ tile: MosaicTile; featured?: boolean }> = ({ tile, featured }) => {
  const image = tileImage(tile);
  const title = tileTitle(tile);
  const subtitle = tileSubtitle(tile);
  const mods =
    tile.kind === 'vehicle' && typeof tile.vehicle.modification_count === 'number'
      ? tile.vehicle.modification_count
      : null;

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-white/[0.08] bg-[#121212] shadow-[0_12px_40px_-16px_rgba(0,0,0,0.85)]"
      aria-hidden
    >
      <div className="relative aspect-[4/3] bg-[#0a0a0a]">
        {image ? (
          <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-xs font-medium">
            Build
          </div>
        )}
        {featured ? (
          <span className="absolute top-2.5 left-2.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] bg-orange-500 text-white">
            Featured
          </span>
        ) : null}
      </div>
      <div className="px-3 py-2.5 border-t border-white/[0.06]">
        <p className="text-white text-sm font-semibold tracking-tight truncate">{title}</p>
        {subtitle ? <p className="text-gray-500 text-xs mt-0.5 truncate">{subtitle}</p> : null}
        {mods != null && mods > 0 ? (
          <p className="text-[11px] text-orange-300/80 mt-1.5 font-mono tabular-nums">{mods} mods</p>
        ) : null}
      </div>
    </div>
  );
};

function splitColumns(tiles: MosaicTile[]): MosaicTile[][] {
  const cols: MosaicTile[][] = [[], [], []];
  tiles.forEach((tile, i) => {
    cols[i % 3].push(tile);
  });
  // Ensure each column has enough height for a seamless loop
  return cols.map((col) => {
    if (col.length === 0) return FALLBACK_TILES.slice(0, 3).map((b) => ({ kind: 'brand' as const, ...b }));
    if (col.length < 3) {
      const pad = FALLBACK_TILES.slice(0, 3 - col.length).map((b) => ({ kind: 'brand' as const, ...b }));
      return [...col, ...pad];
    }
    return col;
  });
}

/**
 * Decorative scrolling build mosaic for the public hero.
 * Pointer-events none so screened copy/CTAs stay the interaction surface.
 */
export const HeroBuildMosaic: React.FC<HeroBuildMosaicProps> = ({
  vehicles,
  featuredVehicleId = null,
}) => {
  const tiles = useMemo<MosaicTile[]>(() => {
    const vehicleTiles: MosaicTile[] = vehicles
      .filter((v) => v?.id)
      .slice(0, 14)
      .map((vehicle) => ({ kind: 'vehicle', vehicle }));

    if (vehicleTiles.length >= 6) return vehicleTiles;

    const needed = Math.max(9 - vehicleTiles.length, 0);
    const brandPads: MosaicTile[] = FALLBACK_TILES.slice(0, needed).map((b) => ({
      kind: 'brand',
      ...b,
    }));
    return [...vehicleTiles, ...brandPads];
  }, [vehicles]);

  const columns = useMemo(() => splitColumns(tiles), [tiles]);

  return (
    <div className="absolute inset-0 overflow-hidden select-none pointer-events-none" aria-hidden>
      <div className="absolute inset-0 flex gap-3 sm:gap-4 px-2 sm:px-4 pt-4 opacity-90">
        {columns.map((col, colIndex) => {
          const duration = colIndex === 1 ? 55 : colIndex === 2 ? 70 : 48;
          const direction = colIndex === 1 ? 'mosaic-scroll-down' : 'mosaic-scroll-up';
          const loop = [...col, ...col];
          return (
            <div
              key={`col-${colIndex}`}
              className={`flex-1 min-w-0 ${colIndex === 2 ? 'hidden md:block' : ''}`}
            >
              <div
                className="flex flex-col gap-3 sm:gap-4 will-change-transform motion-reduce:animate-none"
                style={{
                  animation: `${direction} ${duration}s linear infinite`,
                }}
              >
                {loop.map((tile, i) => {
                  const key =
                    tile.kind === 'vehicle'
                      ? `v-${tile.vehicle.id}-${i}`
                      : `b-${tile.id}-${i}`;
                  const featured =
                    tile.kind === 'vehicle' &&
                    featuredVehicleId != null &&
                    tile.vehicle.id === featuredVehicleId;
                  return <MosaicCard key={key} tile={tile} featured={featured} />;
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HeroBuildMosaic;
